import { supabase } from '../supabase';
import { Session } from '@supabase/supabase-js';

export const MARKET_COMPARISON_CONFIG_TYPE = 'market_comparison_config';

export type MatchVariable = 'variety' | 'format' | 'caliber' | 'color';

export interface VariableGroups {
  variety?: Record<string, string[]>;
  caliber?: Record<string, string[]>;
  format?: Record<string, string[]>;
  color?: Record<string, string[]>;
}

export interface MarketComparisonConfig {
  window_days_before: number;
  window_days_after: number;
  match_variables: MatchVariable[];
  variable_groups: VariableGroups;
}

export const DEFAULT_MARKET_COMPARISON_CONFIG: MarketComparisonConfig = {
  window_days_before: 0,
  window_days_after: 2,
  match_variables: ['variety', 'format', 'caliber', 'color'],
  variable_groups: {},
};

/**
 * Save market comparison config for an exporter (by species and season)
 */
export async function saveMarketComparisonConfig(
  exporterId: number,
  season: string,
  speciesId: number,
  config: MarketComparisonConfig,
  session: Session | null
): Promise<boolean> {
  if (!session) {
    console.error('No session provided');
    return false;
  }

  try {
    const { error } = await supabase
      .from('exporter_settings')
      .upsert({
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        type: MARKET_COMPARISON_CONFIG_TYPE,
        value: config,
      }, {
        onConflict: 'exporter_id,season,species_id,type',
      });

    if (error) {
      console.error('Error saving market comparison config:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error saving market comparison config:', error);
    return false;
  }
}

/**
 * Load market comparison config for an exporter (by species and season)
 * Returns null if no settings exist (first time user)
 */
export async function loadMarketComparisonConfig(
  exporterId: number,
  season: string,
  speciesId: number,
  session: Session | null
): Promise<MarketComparisonConfig | null> {
  if (!session) {
    console.error('No session provided');
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('exporter_settings')
      .select('*')
      .eq('exporter_id', exporterId)
      .eq('season', season)
      .eq('species_id', speciesId)
      .eq('type', MARKET_COMPARISON_CONFIG_TYPE)
      .maybeSingle();

    if (error) {
      console.error('Error loading market comparison config:', error);
      return null;
    }

    return (data?.value as MarketComparisonConfig) || null;
  } catch (error) {
    console.error('Unexpected error loading market comparison config:', error);
    return null;
  }
}
